import { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react'
import { useCrewQuery } from './scheduleQueries'
import { CrewMember } from './scheduleApi'
import { enrichCrewMembers } from './crewModel'
import { useDivisions } from '../../domain/divisions/divisionsQueries'
import { useSalesPeopleQuery } from '../RouteManagement/routingQueries'
import { useRoutingDataQuery } from '../RouteManagement/routingQueries'
import { Route } from '../RouteManagement/routingApi'
import { useUsersQuery } from '../Users/usersQueries'
import { AuthenticationContext } from '../../AuthenticationContext'
import { ScheduleFiltersProvider } from './ScheduleFiltersContext'

export type ScheduleContextType = {
  crew: CrewMember[] | undefined
  isCrewLoading: boolean
  divisions: any[] | undefined
  isDivisionsLoading: boolean
  salesPersons: any[] | undefined
  isSalesPersonsLoading: boolean
  routes: Route[] | undefined
  isRoutesLoading: boolean
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined)

type ScheduleProviderProps = {
  children: ReactNode
}

export const ScheduleProvider = ({ children }: ScheduleProviderProps) => {
  const { currentUser } = useContext(AuthenticationContext)
  const tenantId = currentUser?.tenant?.id
  const { data: crewData, isLoading: isCrewLoading } = useCrewQuery(tenantId)
  const { data: divisions, isLoading: isDivisionsLoading } = useDivisions()
  const { data: salesPersons, isLoading: isSalesPersonsLoading } = useSalesPeopleQuery()
  const { data: routes, isLoading: isRoutesLoading } = useRoutingDataQuery()
  const { data: users } = useUsersQuery(false)
  const [crew, setCrew] = useState<CrewMember[] | undefined>(undefined)

  useEffect(() => {
    if (!crewData || !users) {
      setCrew(undefined)
      return
    }

    setCrew(enrichCrewMembers(crewData, users))
  }, [crewData, users, tenantId])

  const value: ScheduleContextType = useMemo(() => ({
    crew,
    isCrewLoading: isCrewLoading || (!!crewData && !crew),
    divisions,
    isDivisionsLoading,
    salesPersons,
    isSalesPersonsLoading,
    routes,
    isRoutesLoading,
  }), [crew, isCrewLoading, crewData, divisions, isDivisionsLoading, salesPersons, isSalesPersonsLoading, routes, isRoutesLoading])

  return (
    <ScheduleContext.Provider value={value}>
      <ScheduleFiltersProvider>
        {children}
      </ScheduleFiltersProvider>
    </ScheduleContext.Provider>
  )
}

export const useSchedule = () => {
  const context = useContext(ScheduleContext)
  if (context === undefined) {
    throw new Error('useSchedule must be used within a ScheduleProvider')
  }
  return context
}

export default ScheduleContext